import React from 'react';
import {
    Popup,
    useCloseOnEscape,
    useCloseOnOutsideClick,
    useFocusTrap,
    useInitialFocus,
    useReturnFocus,
    usePopupModel
} from '@workday/canvas-kit-react/popup';
import styles from './HeaderMenu.module.scss';
import { autoId } from '../../utils/AutomationUtil';
import { ID_CLOSE_ACCOUNT_POPUP, ID_PROFILE_IMAGE } from '../../autoIds';
import { TertiaryButton } from '@workday/canvas-kit-react/button';
import { caretDownSmallIcon } from '@workday/canvas-system-icons-web';
import menuAvatar from '../../assets/menu_avatar.svg';
import { Avatar } from '../avatar/avatar';

export type MenuContentFn = ({ closeMenu }: { closeMenu: () => void }) => JSX.Element | null;

interface HeaderMenuProps {
    menuContent: MenuContentFn;
    variant?: 'inverse';
    profileImageUrl?: string;
}

export const USER_PROFILE_PHOTO_ALT = 'User Profile';

export const HeaderMenu = ({ variant, profileImageUrl, menuContent }: HeaderMenuProps) => {
    const menuId = `header-menu`;
    const controlButtonId = `${menuId}-button`;
    const isInverse = variant === 'inverse';

    const popupModel = usePopupModel();
    useCloseOnOutsideClick(popupModel);
    useCloseOnEscape(popupModel);
    useInitialFocus(popupModel);
    useReturnFocus(popupModel);
    useFocusTrap(popupModel);

    const visible = popupModel.state.visibility !== 'hidden';

    React.useLayoutEffect(() => {
        if (visible && popupModel.state.stackRef.current) {
            popupModel.state.stackRef.current.setAttribute('id', popupId);
        }
    }, [popupModel.state.stackRef, visible]);

    const popupId = 'popup-test-id';

    React.useLayoutEffect(() => {
        if (visible && popupModel.state.stackRef.current) {
            popupModel.state.stackRef.current.setAttribute('id', popupId);
        }
    }, [popupModel.state.stackRef, visible]);

    const MenuButton = React.forwardRef<HTMLButtonElement>(({ ...props }, ref) => {
        return (
            <TertiaryButton
                ref={ref}
                type='button'
                variant={isInverse ? 'inverse' : undefined}
                className={styles.headerMenuButton}
                aria-controls={menuId}
                id={controlButtonId}
                icon={caretDownSmallIcon}
                iconPosition={'end'}
                {...autoId('headerMenuButton')}
                {...props}>
                <Avatar
                    {...autoId(ID_PROFILE_IMAGE)}
                    className={styles.avatar}
                    url={profileImageUrl}
                    avatarImg={menuAvatar}
                    altText={USER_PROFILE_PHOTO_ALT}
                />
            </TertiaryButton>
        );
    });

    return (
        <Popup model={popupModel}>
            <Popup.Target as={MenuButton} />
            <Popup.Popper placement='bottom'>
                <Popup.Body className={styles.popupBody}>
                    <div aria-owns={popupId} style={{ position: 'absolute' }} />
                    {menuContent({ closeMenu: popupModel.events.hide })}
                    <Popup.CloseIcon
                        {...autoId(ID_CLOSE_ACCOUNT_POPUP)}
                        aria-label='Close'
                        className={styles.popupClose}
                    />
                </Popup.Body>
            </Popup.Popper>
        </Popup>
    );
};
